import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as AssignmentAPI from 'src/api/Assignments';
import * as AssistantAPI from 'src/api/Assistant';
import * as UUID from 'uuid';
import QuillAnimated from 'src/assets/icons/animated/quill';
import {
  AddBlockOperation,
  AddPageOperation,
  SetBlockOperation,
  SetDocumentNameOperation,
  SetImportantWordsOperation
} from 'src/hooks/store/document/operations';
import { useResourceStore } from 'src/hooks/store/resources';
import { useAuthenticatedUser } from 'src/hooks/user';
import { Assignment, Document } from 'src/types/models';
import {
  useApplyDocumentOperation,
  useCreateDocument
} from '../../hooks/document';
import {
  DocumentFormat,
  isValidDocumentFormat,
} from '../../types/DocumentSettings';
import { DOCUMENT_STARTERS } from 'src/configuration/starters';
import { getBlockType } from 'src/configuration/blocks';
import { getWritingPlan } from 'src/configuration/writing-plans';

function postAssignment(assignment: Assignment) {
  return AssignmentAPI.update(assignment.id, assignment);
}

async function createAlignedAssignment(
  document: Document,
  user: {
    id: string;
    name: string;
    surname: string;
  },
): Promise<Assignment> {
  const {
    data: { data },
  } = await AssignmentAPI.create({
    name: document.name,
    document_id: document.id,
  });

  return {
    // Reusing the document ID represents a one-to-one relationship.
    id: data.id,
    name: '',
    instructions: '',
    classrooms: [],
    students: [],
    teacher: user,
    baseDocument: document,
    createdAt: new Date(),
    updatedAt: new Date(),
    notes: [],
  };
}

export default function Align() {
  const navigate = useNavigate();
  const createUninitializedDocument = useCreateDocument(false);
  const createDocument = useCreateDocument(true);
  const setAssignment = useResourceStore((state) => state.setAssignment);
  const apply = useApplyDocumentOperation();
  const user = useAuthenticatedUser();
  const [isCreating, setIsCreating] = React.useState(false);
  const search = new URLSearchParams(useLocation().search);
  const includeBlocks = search.get('includeBlocks') === 'true';
  const includeSentenceStarters = search.get('includeSentenceStarters') === 'true';

  useEffect(() => {
    const topic = search.get('topic') || '';
    const formatParam = search.get('format');
    const format = isValidDocumentFormat(formatParam)
      ? formatParam
      : DocumentFormat.DOC;
    const gradeLevel = search.get('gradeLevel');
    const writingPlanId = search.get('writingPlanId');
    const writingLanguage = search.get('writingLanguage') || 'en-us';
    const supportLanguage = search.get('supportLanguage') || 'en-us';
    const relevantStarters = DOCUMENT_STARTERS.filter(
      (s) =>
        s.format === format &&
        (!writingPlanId || s.writingPlanId === writingPlanId),
    );

    // Document will be undefined until it is created, at which point,
    // redirect the user to the first page.
    if (!isCreating) {
      setIsCreating(true);
      // Run the assistant to generate parts of the document.
      const create = async () => {
        // If the user uploaded an image, we need to fetch it.
        const objectDataUrl = search.get('objectDataUrl');
        let file: Blob | undefined;
        if (objectDataUrl) {
          file = await (await fetch(objectDataUrl)).blob();
        }

        const partialTemplateSelection = AssistantAPI.generateBaseDocument(
          topic,
          relevantStarters,
          file,
          gradeLevel || '4th',
          writingLanguage,
          supportLanguage
        );

        // The result of the assistant streams in. At each step, the whole
        // JSON object is returned, and we will act on each part as it becomes
        // available.
        let documentStarter: typeof DOCUMENT_STARTERS[0] | undefined;
        let document: Document | undefined;
        let assignment: Assignment | undefined;
        let text = '';
        const blockIds: string[] = [];

        for await (const result of partialTemplateSelection) {
          text = result.text;

          if (!documentStarter) {
            // Check to see if we have a complete template.
            documentStarter = relevantStarters.find(
              (s) => s.id === result.starterId,
            );
            if (documentStarter) {
              // Once the template becomes available, create the document.
              if (includeBlocks) {
                document = await createUninitializedDocument(
                  'Untitled',
                  format,
                  {
                    writingPlan: documentStarter.writingPlanId,
                    gradeLevel: gradeLevel || '4th',
                    writingLanguage,
                    supportLanguage,
                  },
                );
                // Add the blocks to the document.
                for (const page of documentStarter.pages) {
                  const pageOp = new AddPageOperation(document.id);
                  apply(pageOp);

                  for (let i = 0; i < page.blocks.length; i += 1) {
                    const block = page.blocks[i];
                    const blockId = UUID.v1();
                    apply(new AddBlockOperation(
                      document.id,
                      pageOp.pageId,
                      blockId,
                      getBlockType(block.typeId),
                      {
                        x: 1,
                        y: i + 1,
                        width: 1,
                        height: 1,
                      }
                    ));

                    // Keep track of the block IDs so that we can use them to
                    // fill in placeholder sentences later.
                    blockIds.push(blockId);
                  }
                }
              } else {
                document = await createDocument(
                  'Untitled',
                  format,
                  {
                    writingPlan: documentStarter.writingPlanId,
                    gradeLevel: gradeLevel || '4th',
                    writingLanguage,
                    supportLanguage,
                  },
                );
              }

              // The document may have been created for an anonymous user,
              // in which case, use the user that was created with the document.
              assignment = await createAlignedAssignment(
                document,
                user || {
                  id: document.user?.id || '',
                  name: '',
                  surname: '',
                },
              );

              // This will navigate away from the quill loading animation,
              // but the rest of the function will continue executing in the
              // background.
              navigate(
                `/document/${document.id}?assignmentPreview=${assignment.id}`,
                { replace: true },
              );
            }
          }

          if (document) {
            if (result.title) {
              apply(new SetDocumentNameOperation(document.id, result.title));

              if (assignment) {
                assignment.name = result.title;
              }
            }

            if (result.writingPrompt) {
              if (assignment) {
                assignment = { ...assignment, instructions: result.writingPrompt };
                setAssignment(assignment);
              }
            }
          }
        }

        if (!(document && assignment)) {
          // This is unlikely, and would constitute an error.
          navigate('/login');
          return;
        }

        await postAssignment(assignment);
        setAssignment({ ...assignment });

        // At this point, the quill loading screen will disappear in favor
        // of the document editor, but we can continue queuing up modifications
        // to the document.
        AssistantAPI.suggestImportantWordsForPrompt(
          assignment?.instructions || 'Free write',
          text,
          documentStarter?.writingPlanId || 'free-write',
          gradeLevel || '4th',
        ).then((response) => {
          const { data } = response.data;
          apply(new SetImportantWordsOperation(document!.id, data[0].words));
        });

        if (includeSentenceStarters && documentStarter) {
          const partialStarters = AssistantAPI.generateSentenceCompletions(
            text,
            documentStarter.pages.flatMap((page) => page.blocks.map((block) => getBlockType(block.typeId))),
            getWritingPlan(documentStarter.writingPlanId) || null,
            assignment,
            gradeLevel || '4th',
          );
          for await (const starters of partialStarters) {
            for (const { index, starter } of starters) {
              apply(
                new SetBlockOperation(document.id, {
                  id: blockIds[index],
                  properties: {
                    placeholder: starter,
                  },
                }),
              );
            }
          }
        }
      };
      create();
    }
  }, [isCreating, user]);

  // Center quill on screen, about 20% of the width
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
      }}
    >
      <div style={{ width: '20%', paddingBottom: '15vh' }}>
        <QuillAnimated />
      </div>
    </div>
  );
}
